import { useAgentMe, useAgentStats } from '~/api/queries/agent';
import type { StandardPercentSource } from '~/types/commissionTiers';

type StandardSplitProfile = {
  tier?: string | null;
  standardSelfClosePercent?: number | null;
  standardReferralPercent?: number | null;
  standardPercentSource?: { selfClose?: StandardPercentSource; referral?: StandardPercentSource } | null;
  eliteGrandfathered?: boolean | null;
};

// The signed-in agent's own Standard split (resolveStandardPercent on the server: tier,
// Elite grandfathering, admin override or UAE programme share). Falls back to the tier row only
// when /me has no profile; callers that print the % wait for isLoaded.
export const useAgentStandardSplit = () => {
  const authStore = useAuthStore();
  const { data: me, isPending: isMePending } = useAgentMe();
  const { data: agentStats } = useAgentStats();
  const { getSelfClose, getReferral } = useCommissionTiersMap();

  const profile = computed<StandardSplitProfile | null>(() => me.value?.agentProfile ?? null);

  const tier = computed<string>(() => profile.value?.tier ?? agentStats.value?.tier ?? 'STARTER');
  // Wait for /me (also during SSR, where it is not fetched): the tier-row fallback would show a
  // UAE agent the INT 50% until it answers. Signed out, /me never runs.
  const isLoaded = computed(() => profile.value != null || !isMePending.value || !authStore.token);

  const selfClosePercent = computed<number>(() => profile.value?.standardSelfClosePercent ?? getSelfClose(tier.value));
  const referralPercent = computed<number>(() => profile.value?.standardReferralPercent ?? getReferral(tier.value));
  const eliteGrandfathered = computed(() => profile.value?.eliteGrandfathered === true);
  // UAE agents self-close on the programme share, not a tier rate.
  const isUaeShare = computed(() => profile.value?.standardPercentSource?.selfClose === 'UAE_CAP');

  return {
    tier,
    isLoaded,
    selfClosePercent,
    referralPercent,
    eliteGrandfathered,
    isUaeShare,
  };
};
